import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Edit2, Trash2, MapPin, Calendar, Plus, X, ChevronDown, Clock, User, MessageSquare, Send, Mail, Phone, FileText, ClipboardList, Shield, Calculator, Paperclip, CheckCheck, ExternalLink, Bell, CheckCircle2, Eye, Pen, Check, CalendarClock, Building2 } from 'lucide-react';
import authService from '../services/authService';
import appointmentService from '../services/appointmentService';
import jobService from '../services/jobService';
import invoiceService from '../services/invoiceService';
import estimateService from '../services/estimateService';
import guaranteeService from '../services/guaranteeService';
import customerService from '../services/customerService';
import treatmentReportsService from '../services/treatmentReportsService';
import { getJobNotes, createJobNote, createJobReminder, toggleNoteReminderStatus, deleteJobNote } from '../services/jobService/api/jobApi';
import emailService from '../services/emailService';
import { VIEWS } from '../App/utils/appConstants';
import MobileFAB from './MobileFAB';
import AddAppointmentModal from './AddAppointmentModal';
import DateTimePicker from './DateTimePicker';
import ComposeEmailModal from './ComposeEmailModal';
import InvoiceForm from './InvoiceList/components/InvoiceForm';
import TreatmentReportEditor from './TreatmentReportEditor';
import WoodwormGuaranteeForm from './WoodwormGuaranteeForm';
import SignatureModal from './SignatureModal';
import TextAreaPopup from './TextAreaPopup';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import { formatDateOrdinal } from '../utils/formatDate';
import { useToast } from './Toast';
import StatusSelect, { JOB_STATUS_OPTIONS } from './StatusSelect';
import AppointmentTimeline from './AppointmentTimeline';
import { useMobileHeader } from '../App/context/MobileHeaderContext';

const ExpandableNote = ({ text, className = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsExpander, setNeedsExpander] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      setNeedsExpander(ref.current.scrollHeight > ref.current.clientHeight);
    }
  }, [text]);

  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
      <p
        ref={!expanded ? ref : undefined}
        className={`text-sm text-gray-600 ${className} ${!expanded ? 'line-clamp-3' : ''}`}
      >
        {text}
      </p>
      {needsExpander && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs text-pest-blue hover:text-pest-teal mt-0.5"
        >
          ...read more
        </button>
      )}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          className="text-xs text-pest-blue hover:text-pest-teal mt-0.5"
        >
          show less
        </button>
      )}
    </div>
  );
};

/** Small + circle context menu for appointment card actions */
const AppointmentCardMenu = ({ onEdit, onDelete, onAddInvoice, onAddEstimate, onAddReport, onAddGuarantee }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 240; // approximate menu height
      const spaceAbove = rect.top;
      const openUp = spaceAbove > menuHeight;
      setMenuStyle({
        position: 'fixed',
        right: window.innerWidth - rect.right,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        zIndex: 9999,
      });
    }
    setOpen(!open);
  };

  const actions = [
    { label: 'Edit Appointment', icon: Edit2, onClick: onEdit },
    { label: 'Add Invoice', icon: FileText, onClick: onAddInvoice },
    { label: 'Add Estimate', icon: Calculator, onClick: onAddEstimate },
    { label: 'Add Report', icon: ClipboardList, onClick: onAddReport },
    { label: 'Add Guarantee', icon: Shield, onClick: onAddGuarantee },
    { label: 'Delete', icon: Trash2, onClick: onDelete, danger: true },
  ];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="w-7 h-7 rounded-full bg-pest-blue text-white flex items-center justify-center hover:bg-blue-600 transition-colors shadow-sm"
      >
        {open ? <X size={14} /> : <Plus size={14} />}
      </button>
      {open && (
        <div style={menuStyle} className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[170px]">
          {actions.map(action => (
            <button
              key={action.label}
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); action.onClick(); }}
              className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${action.danger ? 'text-red-600' : 'text-gray-700'}`}
            >
              <action.icon size={13} className={action.danger ? 'text-red-500' : 'text-pest-blue'} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const JobDetails = ({ job: jobProp, customer: customerProp, onUpdateJob, onDeleteJob, onEditJob, onAddAppointment, appointmentVersion, onNavigateToCustomer, onNavigate, user, userProfile, navigationContext }) => {
  const [fullJob, setFullJob] = useState(null);
  const [fullCustomer, setFullCustomer] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [expandedSection, setExpandedSection] = useState({ future: true, past: false });
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [jobEmails, setJobEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [composeEmailPrefill, setComposeEmailPrefill] = useState('');
  const [actionMenu, setActionMenu] = useState(null); // { appointmentId: string|null } or null
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showEstimateForm, setShowEstimateForm] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [showReportEditor, setShowReportEditor] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [signingReport, setSigningReport] = useState(null);
  const [signingSaving, setSigningSaving] = useState(false);
  const [showGuaranteeForm, setShowGuaranteeForm] = useState(false);
  const [actionContext, setActionContext] = useState({ appointment: null }); // which appointment for the form
  const { confirmDialog, confirm } = useConfirm();
  const toast = useToast();
  const { setMobileActions, clearMobileActions } = useMobileHeader();
  const [notesTab, setNotesTab] = useState(null); // null = auto-detect on load
  const [reminderDatetime, setReminderDatetime] = useState('');
  const [notePopupOpen, setNotePopupOpen] = useState(false);

  // Auto-open invoice form when navigated with openInvoiceForm context
  useEffect(() => {
    if (navigationContext?.openInvoiceForm) {
      setShowInvoiceForm(true);
    }
  }, [navigationContext]);

  // Always fetch full job and customer data from API
  useEffect(() => {
    if (!jobProp?.id || !user) return;
    const franchiseeId = getFranchiseeIdForDataLoading(user);

    setLoadingJob(true);
    jobService.getJob(franchiseeId, jobProp.id).then(data => {
      const jobData = data.job || data;
      setFullJob(jobData);
    }).catch(err => {
      console.error('Error fetching full job:', err);
    }).finally(() => setLoadingJob(false));

    // Fetch full customer separately
    const customerId = jobProp.customer_id || customerProp?.id;
    if (customerId) {
      customerService.getCustomer(franchiseeId, customerId).then(result => {
        const cust = result?.customer || result;
        if (cust?.id) setFullCustomer(cust);
      }).catch(err => {
        console.error('Error fetching full customer:', err);
      });
    }
  }, [jobProp?.id, user]);

  // Use fetched job if available, otherwise use prop
  const job = fullJob || jobProp;
  const customer = fullCustomer || customerProp;

  const [jobStatus, setJobStatus] = useState(job?.status || 'active');

  // Sync local status when job prop changes (e.g. navigating to different job)
  useEffect(() => {
    if (job?.status) setJobStatus(job.status);
  }, [job?.id, job?.status]);

  // Shared job status change handler
  const handleJobStatusChange = useCallback((newStatus) => {
    const oldStatus = jobStatus;
    setJobStatus(newStatus); // optimistic update
    const franchiseeId = getFranchiseeIdForCRUD(user, job?.franchisee_id);
    jobService.updateJob(franchiseeId, job.id, { status: newStatus }).then(() => {
      if (onUpdateJob) onUpdateJob({ ...job, status: newStatus });
      const label = newStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      toast.success(`Job status updated to ${label}`);
    }).catch(err => {
      console.error('Error updating job status:', err);
      setJobStatus(oldStatus); // revert on failure
      toast.error('Failed to update job status');
    });
  }, [job, jobStatus, user, onUpdateJob, toast]);

  useEffect(() => {
    if (job) {
      setMobileActions(
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-bold text-gray-800">Status:</span>
          <StatusSelect
            value={jobStatus || 'active'}
            onChange={handleJobStatusChange}
            options={JOB_STATUS_OPTIONS}
            size="sm"
          />
        </div>
      );
    }
    return () => clearMobileActions();
  }, [job, jobStatus, handleJobStatusChange, setMobileActions, clearMobileActions]);

  // Load appointments, notes, documents on mount
  useEffect(() => {
    if (job && job.id && user) {
      loadAppointments();
      loadNotes();
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  // Reload appointments when a new one is created (version bumped)
  useEffect(() => {
    if (appointmentVersion && job?.id && user) {
      loadAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentVersion]);

  // Load emails when customer becomes available (may load after job)
  useEffect(() => {
    if (job?.id && customer?.id && user) {
      loadEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, customer?.id]);

  const loadAppointments = async () => {
    if (!user || !job?.id) return;

    try {
      setLoadingAppointments(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const response = await appointmentService.getAppointments(franchiseeId, {
        job_id: job.id,
        sortBy: 'scheduled_date',
        sortOrder: 'ASC'
      });
      setAppointments(response.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const loadNotes = async () => {
    if (!user || !job?.id) return;
    try {
      setLoadingNotes(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const response = await getJobNotes(franchiseeId, job.id);
      const loadedNotes = response.notes || [];
      setNotes(loadedNotes);
      // Auto-open reminders tab if any active reminders are due
      if (notesTab === null) {
        const hasOverdue = loadedNotes.some(n => n.is_reminder && n.reminder_status === 'active' && new Date(n.reminder_datetime) <= new Date());
        setNotesTab(hasOverdue ? 'reminders' : 'notes');
      }
    } catch (error) {
      console.error('Error loading job notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadDocuments = async () => {
    if (!user || !job?.id) return;
    try {
      setLoadingDocuments(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const result = await jobService.getJobDocuments(franchiseeId, job.id);
      setDocuments(result);
    } catch (error) {
      console.error('Error loading job documents:', error);
      setDocuments({ invoices: [], treatmentReports: [], guarantees: [] });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadEmails = async () => {
    if (!user || !customer?.id) return;
    try {
      setLoadingEmails(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const response = await emailService.getCustomerEmails(franchiseeId, customer.id);
      // Only show emails linked to this specific job
      const allEmails = response.emails || [];
      setJobEmails(allEmails.filter(e => e.job_id === job.id));
    } catch (error) {
      console.error('Error loading emails:', error);
      setJobEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  const formatEmailDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim() || submittingNote) return;
    try {
      setSubmittingNote(true);
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      await createJobNote(franchiseeId, job.id, newNoteText.trim());
      setNewNoteText('');
      loadNotes();
      toast.success('Note added');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleAddReminder = async () => {
    if (!newNoteText.trim() || !reminderDatetime || submittingNote) return;
    try {
      setSubmittingNote(true);
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      // datetime-local gives 'YYYY-MM-DDTHH:MM' — replace T with space for MySQL
      const mysqlDatetime = reminderDatetime.replace('T', ' ') + ':00';
      await createJobReminder(franchiseeId, job.id, newNoteText.trim(), mysqlDatetime);
      setNewNoteText('');
      setReminderDatetime('');
      loadNotes();
      toast.success('Reminder added');
    } catch (error) {
      console.error('Error adding reminder:', error);
      toast.error('Failed to add reminder');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleToggleReminder = async (noteId, currentStatus) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      const newStatus = currentStatus === 'active' ? 'done' : 'active';
      await toggleNoteReminderStatus(franchiseeId, job.id, noteId, newStatus);
      loadNotes();
      toast.success(newStatus === 'done' ? 'Reminder completed' : 'Reminder reactivated');
    } catch (error) {
      console.error('Error toggling reminder:', error);
      toast.error('Failed to update reminder');
    }
  };

  const handleDeleteNote = async (noteId) => {
    const ok = await confirm({
      title: 'Delete Note',
      message: 'Are you sure you want to delete this note?',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      await deleteJobNote(franchiseeId, job.id, noteId);
      loadNotes();
      toast.success('Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  if (loadingJob) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto"></div>
          <p className="text-gray-400 mt-3">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job || !customer) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Job not found</p>
        </div>
      </div>
    );
  }

  // Status configuration
  const statusConfig = {
    active: { label: 'Active', color: 'bg-blue-100 text-blue-800' },
    on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-800' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' }
  };

  const currentStatus = statusConfig[jobStatus] || statusConfig.active;

  // Helper function to format treatment address
  const formatTreatmentAddress = () => {
    const parts = [];
    if (job.treatment_address_line1) parts.push(job.treatment_address_line1);
    if (job.treatment_address_line2) parts.push(job.treatment_address_line2);
    if (job.treatment_address_city) parts.push(job.treatment_address_city);
    if (job.treatment_address_postcode) parts.push(job.treatment_address_postcode);
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  // Check if treatment contact is different from customer
  const hasTreatmentContact = job.treatment_contact_name || job.treatment_contact_company || job.treatment_contact_email || job.treatment_contact_phone;

  // Separate future and past appointments
  const now = new Date();
  const futureAppointments = appointments.filter(apt => new Date(apt.scheduled_date) >= now)
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
  const pastAppointments = appointments.filter(apt => new Date(apt.scheduled_date) < now)
    .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
  const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
  const firstAppointment = [...appointments].sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))[0];

  const handleDeleteJob = async () => {
    const ok = await confirm({
      title: 'Delete Job',
      message: 'This will permanently delete this job and cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (ok) onDeleteJob(job.id);
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
  };

  const handleUpdateAppointment = async (appointmentData) => {
    try {
      // Use the appointment's own franchisee_id so admin can edit any franchisee's appointments
      const franchiseeId = getFranchiseeIdForCRUD(user, editingAppointment?.franchisee_id);
      await appointmentService.updateAppointment(franchiseeId, appointmentData.id, appointmentData);
      setEditingAppointment(null);
      loadAppointments();
      toast.success('Appointment updated');
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment: ' + (error.message || 'Unknown error'));
    }
  };

  const handleStatusChange = async (appointment, newStatus) => {
    const oldStatus = appointment.status;
    // Update locally immediately
    setAppointments(prev => prev.map(apt =>
      apt.id === appointment.id ? { ...apt, status: newStatus } : apt
    ));
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, appointment.franchisee_id);
      await appointmentService.updateAppointment(franchiseeId, appointment.id, { status: newStatus });
      const label = newStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      toast.success(`Appointment status updated to ${label}`);
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert on failure
      setAppointments(prev => prev.map(apt =>
        apt.id === appointment.id ? { ...apt, status: oldStatus } : apt
      ));
      toast.error('Failed to update appointment status');
    }
  };

  const handleDeleteAppointment = async (appointment) => {
    const ok = await confirm({
      title: 'Delete Appointment',
      message: `Delete this appointment on ${new Date(appointment.scheduled_date).toLocaleDateString()}? This cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      // Use the appointment's own franchisee_id so admin can delete any franchisee's appointments
      const franchiseeId = getFranchiseeIdForCRUD(user, appointment.franchisee_id);
      await appointmentService.deleteAppointment(franchiseeId, appointment.id);
      loadAppointments();
      toast.success('Appointment deleted');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  // Format appointment type for display
  const formatAppointmentType = (type) => {
    if (!type) return null;
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Look up appointment for a document
  const getDocAppointment = (doc) => {
    if (!doc.appointment_id) return null;
    return appointments.find(a => a.id === doc.appointment_id) || null;
  };

  // Get all documents linked to a specific appointment
  const getAppointmentDocs = (appointmentId) => {
    if (!documents) return [];
    const docs = [];
    (documents.invoices || []).forEach(d => { if (d.appointment_id === appointmentId) docs.push({ ...d, _type: 'invoice' }); });
    (documents.treatmentReports || []).forEach(d => { if (d.appointment_id === appointmentId) docs.push({ ...d, _type: 'report' }); });
    (documents.estimates || []).forEach(d => { if (d.appointment_id === appointmentId) docs.push({ ...d, _type: 'estimate' }); });
    (documents.guarantees || []).forEach(d => { if (d.appointment_id === appointmentId) docs.push({ ...d, _type: 'guarantee' }); });
    return docs;
  };

  // ── Document Action Menu & Handlers ──────────────────────────────────

  const openAction = (formSetter, appointment = null) => {
    setActionContext({ appointment });
    setActionMenu(null);
    formSetter(true);
  };

  const handleInvoiceSubmit = async (invoiceData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      if (editingInvoice) {
        await invoiceService.updateInvoice(franchiseeId, editingInvoice.id, invoiceData);
        toast.success('Invoice updated successfully');
      } else {
        const dataWithContext = {
          ...invoiceData,
          job_id: job.id,
          appointment_id: actionContext.appointment?.id || null
        };
        await invoiceService.createInvoice(franchiseeId, dataWithContext);
        toast.success(`Invoice ${invoiceData.invoice_number || ''} created successfully`);
      }
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      setActionContext({ appointment: null });
      loadDocuments();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(`Error saving invoice: ${error.message}`);
    }
  };

  const handleEstimateSubmit = async (estimateData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      if (editingEstimate) {
        await estimateService.updateEstimate(franchiseeId, editingEstimate.id, estimateData);
        toast.success('Estimate updated successfully');
      } else {
        await estimateService.createEstimate(franchiseeId, estimateData);
        toast.success('Estimate created successfully');
      }
      setShowEstimateForm(false);
      setEditingEstimate(null);
      setActionContext({ appointment: null });
      loadDocuments();
    } catch (error) {
      console.error('Error saving estimate:', error);
      toast.error(`Error saving estimate: ${error.message}`);
    }
  };

  const handleReportSave = async () => {
    setShowReportEditor(false);
    setEditingReport(null);
    setActionContext({ appointment: null });
    loadDocuments();
  };

  // Save & Sign: save the report, then immediately open signing modal
  const handleSaveAndSign = async (savedReport) => {
    setShowReportEditor(false);
    setEditingReport(null);
    setActionContext({ appointment: null });
    loadDocuments();
    // Open signing modal with the saved report
    if (savedReport?.id) {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      try {
        const fullReport = await treatmentReportsService.getTreatmentReport(franchiseeId, savedReport.id);
        setSigningReport(fullReport.report || fullReport);
      } catch (error) {
        console.error('Error loading report for signing:', error);
        toast.error('Report saved but failed to open signing');
      }
    }
  };

  const handleViewReportPdf = async (report) => {
    const franchiseeId = getFranchiseeIdForDataLoading(user);
    try {
      const result = await treatmentReportsService.generatePDF(franchiseeId, report.id);
      if (result.pdf_url) {
        window.open(result.pdf_url, '_blank');
      } else {
        toast.error('PDF not available');
      }
    } catch (error) {
      console.error('Error generating report PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleEditReport = async (report) => {
    const franchiseeId = getFranchiseeIdForDataLoading(user);
    try {
      const fullReport = await treatmentReportsService.getTreatmentReport(franchiseeId, report.id);
      setEditingReport(fullReport.report || fullReport);
      setShowReportEditor(true);
    } catch (error) {
      console.error('Error loading report for editing:', error);
      toast.error('Failed to load treatment report');
    }
  };

  const handleSignReport = async (report) => {
    const franchiseeId = getFranchiseeIdForDataLoading(user);
    try {
      const fullReport = await treatmentReportsService.getTreatmentReport(franchiseeId, report.id);
      setSigningReport(fullReport.report || fullReport);
    } catch (error) {
      console.error('Error loading report for signing:', error);
      toast.error('Failed to load treatment report');
    }
  };

  const handleSignatureSubmit = async (signatureData) => {
    const franchiseeId = getFranchiseeIdForDataLoading(user);
    const reportId = signingReport?.id;
    if (!reportId) return;

    setSigningSaving(true);
    try {
      await treatmentReportsService.signReport(franchiseeId, reportId, signatureData);
      // Regenerate PDF so it includes the customer signature
      try {
        await treatmentReportsService.generatePDF(franchiseeId, reportId);
      } catch (pdfErr) {
        console.error('PDF regeneration after signing failed:', pdfErr);
      }
      setSigningReport(null);
      loadDocuments();
      toast.success('Treatment report signed successfully');
    } catch (error) {
      console.error('Error signing report:', error);
      toast.error('Failed to save signature');
    } finally {
      setSigningSaving(false);
    }
  };

  const handleGuaranteeSubmit = async (guaranteeData) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      const result = await guaranteeService.createGuarantee(franchiseeId, guaranteeData);
      setShowGuaranteeForm(false);
      setActionContext({ appointment: null });
      loadDocuments();
      toast.success(`Guarantee ${result.guarantee_number} created successfully`);
    } catch (error) {
      console.error('Error creating guarantee:', error);
      toast.error(`Error creating guarantee: ${error.message}`);
    }
  };

  // Document Action Menu — context menu for existing documents
  const DocActionMenu = ({ doc, docType }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    const updatePosition = useCallback(() => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const dropH = dropRef.current ? dropRef.current.offsetHeight : 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openBelow = spaceBelow >= dropH || spaceBelow > spaceAbove;
      setPos({
        top: openBelow ? rect.bottom + 4 : rect.top - dropH - 4,
        left: rect.right - 200,
      });
    }, []);

    useEffect(() => {
      if (!open) return;
      updatePosition();
      const handleClick = (e) => {
        if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
        setOpen(false);
      };
      const handleScroll = () => updatePosition();
      document.addEventListener('mousedown', handleClick);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClick);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [open, updatePosition]);

    const handleViewInvoicePdf = async (inv) => {
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      try {
        await invoiceService.viewPDF(franchiseeId, inv.id);
      } catch (error) {
        console.error('Error viewing invoice PDF:', error);
        toast.error('Failed to open invoice PDF');
      }
    };

    const handleViewEstimatePdf = async (est) => {
      const fId = getFranchiseeIdForDataLoading(user);
      try {
        await estimateService.viewPDF(fId, est.id);
      } catch (error) {
        console.error('Error viewing estimate PDF:', error);
        toast.error('Failed to open estimate PDF');
      }
    };

    const handleViewGuaranteePdf = async (g) => {
      const fId = getFranchiseeIdForDataLoading(user);
      try {
        await guaranteeService.viewPDF(fId, g.id);
      } catch (error) {
        console.error('Error viewing guarantee PDF:', error);
        toast.error('Failed to open guarantee PDF');
      }
    };

    const handleEditInvoice = async (inv) => {
      const fId = getFranchiseeIdForDataLoading(user);
      try {
        const result = await invoiceService.getInvoice(fId, inv.id);
        const fullInvoice = result.invoice || result;
        setEditingInvoice(fullInvoice);
        setShowInvoiceForm(true);
      } catch (error) {
        console.error('Error loading invoice for edit:', error);
        toast.error('Failed to load invoice');
      }
    };

    const handleEditEstimate = async (est) => {
      const fId = getFranchiseeIdForDataLoading(user);
      try {
        const result = await estimateService.getEstimate(fId, est.id);
        const fullEstimate = result.estimate || result;
        setEditingEstimate(fullEstimate);
        setShowEstimateForm(true);
      } catch (error) {
        console.error('Error loading estimate for edit:', error);
        toast.error('Failed to load estimate');
      }
    };

    const items = [];
    if (docType === 'invoice') {
      items.push({ label: 'View PDF', icon: Eye, action: () => handleViewInvoicePdf(doc) });
      items.push({ label: 'Edit Invoice', icon: Edit2, action: () => handleEditInvoice(doc) });
    }
    if (docType === 'estimate') {
      items.push({ label: 'View PDF', icon: Eye, action: () => handleViewEstimatePdf(doc) });
      items.push({ label: 'Edit Estimate', icon: Edit2, action: () => handleEditEstimate(doc) });
    }
    if (docType === 'report') {
      items.push({ label: 'View PDF', icon: Eye, action: () => handleViewReportPdf(doc) });
      if (!doc.customer_name_signed) {
        items.push({ label: 'Edit Report', icon: Edit2, action: () => handleEditReport(doc) });
        items.push({ label: 'Sign Report', icon: Pen, action: () => handleSignReport(doc) });
      }
    }
    if (docType === 'guarantee') {
      items.push({ label: 'View PDF', icon: Eye, action: () => handleViewGuaranteePdf(doc) });
    }

    if (items.length === 0) return null;

    return (
      <div className="relative flex-shrink-0">
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className="btn btn-sm btn-secondary !gap-1"
        >
          <ChevronDown size={14} />
          <span className="hidden sm:inline">Actions</span>
        </button>
        {open && ReactDOM.createPortal(
          <div
            ref={dropRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[180px]"
            style={{ zIndex: 9999, top: pos.top, left: Math.max(8, pos.left) }}
          >
            <div className="px-2 py-1 space-y-1">
              {items.map(({ label, icon: Icon, action }) => (
                <button
                  key={label}
                  onClick={(e) => { e.stopPropagation(); setOpen(false); action(); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-white rounded-lg bg-pest-blue hover:bg-blue-700 transition-colors"
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Action Menu dropdown component
  const ActionMenu = ({ preselectedAppointment = null, alignLeft = false }) => {
    const [open, setOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(preselectedAppointment?.id || '');
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    const updatePosition = useCallback(() => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const dropH = dropRef.current ? dropRef.current.offsetHeight : 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openBelow = alignLeft ? true : spaceBelow >= dropH || spaceBelow > spaceAbove;
      setPos({
        top: openBelow ? rect.bottom + 4 : rect.top - dropH - 4,
        left: alignLeft ? rect.left : rect.right - 260,
      });
    }, [alignLeft]);

    useEffect(() => {
      if (!open) return;
      updatePosition();
      const handleClick = (e) => {
        if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
        setOpen(false);
      };
      const handleScroll = () => updatePosition();
      document.addEventListener('mousedown', handleClick);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClick);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [open, updatePosition]);

    const getSelectedApt = () => {
      if (!selectedAppointment) return null;
      return appointments.find(a => a.id === selectedAppointment) || null;
    };

    const items = [
      { label: 'Invoice', icon: FileText, bg: 'bg-green-600 hover:bg-green-700', action: () => openAction(setShowInvoiceForm, getSelectedApt()) },
      { label: 'Estimate', icon: Calculator, bg: 'bg-teal-600 hover:bg-teal-700', action: () => openAction(setShowEstimateForm, getSelectedApt()) },
      { label: 'Treatment Report', icon: ClipboardList, bg: 'bg-blue-600 hover:bg-blue-700', action: () => openAction(setShowReportEditor, getSelectedApt()) },
      { label: 'Guarantee', icon: Shield, bg: 'bg-indigo-600 hover:bg-indigo-700', action: () => openAction(setShowGuaranteeForm, getSelectedApt()) },
    ];

    return (
      <div className="relative">
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className="btn btn-md btn-secondary !gap-1"
          title="Add Document"
        >
          <Plus size={16} />
          <span>Add Document</span>
        </button>
        {open && ReactDOM.createPortal(
          <div
            ref={dropRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[260px]"
            style={{ zIndex: 9999, top: pos.top, left: Math.max(8, pos.left) }}
          >
            {/* Create Document For dropdown */}
            {appointments.length > 0 && (
              <div className="px-3 py-2 border-b border-gray-100">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Create Document For:</label>
                <select
                  value={selectedAppointment}
                  onChange={(e) => setSelectedAppointment(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">Job only (no appointment)</option>
                  {appointments.map(apt => {
                    const d = new Date(apt.scheduled_date);
                    const label = `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}${apt.appointment_type ? ' - ' + formatAppointmentType(apt.appointment_type) : ''}`;
                    return <option key={apt.id} value={apt.id}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            <div className="px-3 pt-2 pb-1 space-y-1.5">
              {items.map(({ label, icon: Icon, bg, action }) => (
                <button
                  key={label}
                  onClick={(e) => { e.stopPropagation(); setOpen(false); action(); }}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-white rounded-lg ${bg} transition-colors`}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Compact "+ Add" button for Documents card header
  const AddDocHeaderButton = () => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    const updatePosition = useCallback(() => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const dropW = 220;
      const dropH = dropRef.current ? dropRef.current.offsetHeight : 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openBelow = spaceBelow >= dropH || spaceBelow > rect.top;
      // Align right edge to button right edge, clamp to viewport
      const left = Math.max(8, Math.min(rect.right - dropW, window.innerWidth - dropW - 8));
      setPos({
        top: openBelow ? rect.bottom + 4 : rect.top - dropH - 4,
        left,
      });
    }, []);

    useEffect(() => {
      if (!open) return;
      updatePosition();
      const handleClick = (e) => {
        if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
        setOpen(false);
      };
      const handleScroll = () => updatePosition();
      document.addEventListener('mousedown', handleClick);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClick);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [open, updatePosition]);

    const [selectedApt, setSelectedApt] = useState('');

    const getSelectedAppointment = () => {
      if (!selectedApt) return null;
      return appointments.find(a => a.id === selectedApt) || null;
    };

    const items = [
      { label: 'Invoice', icon: FileText, bg: 'bg-green-600 hover:bg-green-700', action: () => openAction(setShowInvoiceForm, getSelectedAppointment()) },
      { label: 'Estimate', icon: Calculator, bg: 'bg-teal-600 hover:bg-teal-700', action: () => openAction(setShowEstimateForm, getSelectedAppointment()) },
      { label: 'Treatment Report', icon: ClipboardList, bg: 'bg-blue-600 hover:bg-blue-700', action: () => openAction(setShowReportEditor, getSelectedAppointment()) },
      { label: 'Guarantee', icon: Shield, bg: 'bg-indigo-600 hover:bg-indigo-700', action: () => openAction(setShowGuaranteeForm, getSelectedAppointment()) },
    ];

    return (
      <div className="relative">
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-pest-blue text-white text-sm font-medium rounded-lg hover:bg-pest-teal transition-colors"
        >
          <Plus size={14} /> Add
        </button>
        {open && ReactDOM.createPortal(
          <div
            ref={dropRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[220px]"
            style={{ zIndex: 9999, top: pos.top, left: pos.left }}
          >
            {appointments.length > 0 && (
              <div className="px-3 pb-2 mb-1 border-b border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1">Create Document For:</label>
                <select
                  value={selectedApt}
                  onChange={(e) => setSelectedApt(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-pest-blue focus:border-pest-blue"
                >
                  <option value="">Job only (no appointment)</option>
                  {appointments.map(apt => {
                    const d = new Date(apt.scheduled_date);
                    const label = `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}${apt.appointment_type ? ' - ' + formatAppointmentType(apt.appointment_type) : ''}`;
                    return <option key={apt.id} value={apt.id}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            <div className="px-2 py-1 space-y-1">
              {items.map(({ label, icon: Icon, bg, action }) => (
                <button
                  key={label}
                  onClick={(e) => { e.stopPropagation(); setOpen(false); action(); }}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-white rounded-lg ${bg} transition-colors`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div className="px-0 py-2 md:p-6">
      <MobileFAB actions={[
        { label: 'Add Appointment', icon: Calendar, onClick: () => onAddAppointment({ jobId: job.id, job }) },
        { label: 'Email', icon: Mail, onClick: () => setShowComposeEmail(true) },
        { label: 'Edit Job', icon: Edit2, onClick: () => onEditJob(job) },
        { label: 'Add Invoice', icon: FileText, onClick: () => openAction(setShowInvoiceForm) },
        { label: 'Add Report', icon: ClipboardList, onClick: () => openAction(setShowReportEditor) },
        { label: 'Add Estimate', icon: Calculator, onClick: () => openAction(setShowEstimateForm) },
        { label: 'Add Guarantee', icon: Shield, onClick: () => openAction(setShowGuaranteeForm) },
        ...((authService.isAdmin() || authService.hasAdminReturn()) ? [{ label: 'Delete', icon: Trash2, onClick: handleDeleteJob, danger: true }] : [])
      ]} />
      {/* Header */}
      <div className="mb-4 md:mb-6 px-3 md:px-0">
        <div className="flex items-center space-x-4 mb-3">
          <h1 className="hidden lg:block text-2xl md:text-3xl font-bold text-gray-900">Job Details</h1>
        </div>

        <div className="hidden md:flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Job Status:</span>
            <StatusSelect
              value={jobStatus || 'active'}
              onChange={handleJobStatusChange}
              options={JOB_STATUS_OPTIONS}
              size="lg"
            />
          </div>
          <button
            onClick={() => setShowComposeEmail(true)}
            className="btn btn-md btn-secondary"
          >
            <Mail size={16} />
            <span>Email</span>
          </button>
          <button
            onClick={() => onEditJob(job)}
            className="btn btn-md btn-secondary"
          >
            <Edit2 size={16} />
            <span>Edit Job</span>
          </button>
          {(authService.isAdmin() || authService.hasAdminReturn()) && (
            <button
              onClick={handleDeleteJob}
              className="btn btn-md btn-danger"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          )}
          <ActionMenu alignLeft />
        </div>
      </div>

      {/* 3-Column Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 md:gap-6">

        {/* Left Column - Job Overview */}
        <div className="space-y-0 md:space-y-6 order-1 lg:order-1">
          {/* Job Information Card */}
          <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden border-b md:border-b-0 border-gray-200">
            <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-pest-blue">
                {job.contract_start_date ? 'Contract' : 'Job'} Information
              </h2>
              {job.estimated_visits && (
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {job.estimated_visits} Visit {job.contract_start_date ? 'Contract' : 'Job'}
                </p>
              )}
            </div>

            <div className="p-3 sm:p-6 space-y-4">
              {/* Billing Customer */}
              <div>
                <p className="text-sm text-gray-600 mb-1">Billing Customer</p>
                <button
                  onClick={() => onNavigateToCustomer(customer)}
                  className="font-medium text-pest-blue hover:text-pest-teal underline transition-colors text-left"
                >
                  {customer.name}
                </button>
                {customer.created_at && (
                  <span className="text-xs text-gray-500 ml-1">(since {formatDateOrdinal(customer.created_at)})</span>
                )}
              </div>

              {/* Treatment Details */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Treatment Details</p>
                <div className="space-y-1.5">
                  {job.treatment_contact_name && (
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-gray-900">{job.treatment_contact_name}</span>
                    </div>
                  )}
                  {job.treatment_contact_company && (
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{job.treatment_contact_company}</span>
                    </div>
                  )}
                  {job.treatment_contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-gray-400 flex-shrink-0" />
                      <a href={`tel:${job.treatment_contact_phone}`} className="text-sm text-pest-blue hover:underline">
                        {job.treatment_contact_phone}
                      </a>
                    </div>
                  )}
                  {job.treatment_contact_email && (
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-gray-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => { setComposeEmailPrefill(job.treatment_contact_email); setShowComposeEmail(true); }}
                        className="text-sm text-pest-blue hover:underline"
                      >
                        {job.treatment_contact_email}
                      </button>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{formatTreatmentAddress()}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {job.description && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p className="text-gray-700">{job.description}</p>
                </div>
              )}

              {/* Price */}
              {job.price && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Price</p>
                  <p className="text-2xl font-bold text-gray-900">
                    £{parseFloat(job.price).toFixed(2)}
                    {job.price_type && job.price_type !== 'total' && (
                      <span className="text-sm font-medium text-gray-500 ml-1">
                        / {job.price_type === 'per_visit' ? 'visit' : job.price_type === 'per_year' ? 'year' : job.price_type}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Visit Progress */}
              {completedAppointments > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Visit Progress</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {completedAppointments}{job.estimated_visits ? ` / ${job.estimated_visits}` : ''} completed
                  </p>
                </div>
              )}

              {/* Dates */}
              <div className="pt-4 border-t border-gray-200 space-y-1">
                {job.contract_start_date ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Contract Start:</span>
                    <span className="text-gray-900">{formatDateOrdinal(job.contract_start_date)}</span>
                  </div>
                ) : firstAppointment ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Job Start:</span>
                    <span className="text-gray-900">{formatDateOrdinal(firstAppointment.scheduled_date)}</span>
                  </div>
                ) : null}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-gray-900">{formatDateOrdinal(job.created_at)}</span>
                </div>
                {job.updated_at && job.updated_at !== job.created_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="text-gray-900">{formatDateOrdinal(job.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Job Notes / Reminders Card */}
          <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden border-b md:border-b-0 border-gray-200">
            <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <MessageSquare size={20} className="text-pest-blue" />
                  <h2 className="text-xl font-semibold text-pest-blue">Notes / Reminders</h2>
                </div>
              </div>
              {/* Tabs */}
              <div className="flex gap-1 bg-gray-200 rounded-lg p-0.5">
                <button
                  onClick={() => setNotesTab('notes')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    notesTab === 'notes' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <MessageSquare size={14} />
                  Notes
                  {notes.filter(n => !n.is_reminder).length > 0 && (
                    <span className="bg-pest-blue text-white w-5 h-5 rounded-full text-[10px] inline-flex items-center justify-center font-bold">
                      {notes.filter(n => !n.is_reminder).length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setNotesTab('reminders')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    notesTab === 'reminders' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Bell size={14} />
                  Reminders
                  {notes.filter(n => n.is_reminder && n.reminder_status === 'active').length > 0 && (
                    <span className="bg-red-500 text-white w-5 h-5 rounded-full text-[10px] inline-flex items-center justify-center font-bold">
                      {notes.filter(n => n.is_reminder && n.reminder_status === 'active').length}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="p-3 sm:p-6 space-y-4">
              {notesTab === 'notes' ? (
                <>
                  {/* Add Note Form */}
                  <div>
                    <TextAreaPopup
                      label="Note"
                      value={newNoteText}
                      onChange={setNewNoteText}
                      placeholder="Add a note..."
                      fieldKey="job_note"
                      isOpen={notePopupOpen && notesTab === 'notes'}
                      onOpen={() => setNotePopupOpen(true)}
                      onClose={() => setNotePopupOpen(false)}
                    />
                    <div className="flex items-center justify-end mt-2">
                      <button
                        onClick={handleAddNote}
                        disabled={!newNoteText.trim() || submittingNote}
                        className="btn btn-sm btn-secondary disabled:opacity-50"
                      >
                        <Send size={14} />
                        <span>{submittingNote ? 'Adding...' : 'Add Note'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Notes List */}
                  {loadingNotes ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pest-blue mx-auto"></div>
                      <p className="text-gray-500 mt-2 text-sm">Loading notes...</p>
                    </div>
                  ) : notes.filter(n => !n.is_reminder).length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-4">No notes yet</p>
                  ) : (
                    <div className="space-y-3">
                      {notes.filter(n => !n.is_reminder).map((note) => (
                        <div key={note.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <p className="text-gray-700 text-sm whitespace-pre-wrap">{note.note_text}</p>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <User size={12} />
                              <span>{note.author_name || 'Unknown'}</span>
                              <span>-</span>
                              <span>
                                {formatDateOrdinal(note.created_at)}{' '}
                                {new Date(note.created_at).toLocaleTimeString('en-GB', {
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete note"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Add Reminder Form */}
                  <div>
                    <TextAreaPopup
                      label="Reminder"
                      value={newNoteText}
                      onChange={setNewNoteText}
                      placeholder="What needs to be done?"
                      fieldKey="job_reminder"
                      isOpen={notePopupOpen && notesTab === 'reminders'}
                      onOpen={() => setNotePopupOpen(true)}
                      onClose={() => setNotePopupOpen(false)}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <DateTimePicker
                        value={reminderDatetime}
                        onChange={setReminderDatetime}
                        className="flex-1"
                      />
                      <button
                        onClick={handleAddReminder}
                        disabled={!newNoteText.trim() || !reminderDatetime || submittingNote}
                        className="btn btn-sm btn-secondary disabled:opacity-50"
                      >
                        <Bell size={14} />
                        <span>{submittingNote ? 'Adding...' : 'Add Reminder'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Reminders List */}
                  {loadingNotes ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pest-blue mx-auto"></div>
                      <p className="text-gray-500 mt-2 text-sm">Loading reminders...</p>
                    </div>
                  ) : notes.filter(n => n.is_reminder).length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-4">No reminders yet</p>
                  ) : (
                    <div className="space-y-3">
                      {notes.filter(n => n.is_reminder)
                        .sort((a, b) => {
                          // Active first, then by datetime
                          if (a.reminder_status !== b.reminder_status) {
                            return a.reminder_status === 'active' ? -1 : 1;
                          }
                          return new Date(a.reminder_datetime) - new Date(b.reminder_datetime);
                        })
                        .map((note) => {
                          const overdue = note.reminder_status === 'active' && new Date(note.reminder_datetime) <= new Date();
                          const isDone = note.reminder_status === 'done';
                          return (
                            <div
                              key={note.id}
                              className={`border rounded-lg p-3 transition-colors ${
                                overdue ? 'border-red-300' : 'border-gray-200'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap text-gray-700">
                                {note.note_text}
                              </p>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                                <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                                  <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : ''}`}>
                                    <Clock size={12} />
                                    {formatDateOrdinal(note.reminder_datetime)}{' '}
                                    {new Date(note.reminder_datetime).toLocaleTimeString('en-GB', {
                                      hour: '2-digit', minute: '2-digit'
                                    })}
                                  </span>
                                  {overdue && <span className="text-red-600 font-semibold">Overdue</span>}
                                  <span className="text-gray-400">by {note.author_name || 'Unknown'}</span>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {isDone ? (
                                    <button
                                      onClick={() => handleToggleReminder(note.id, note.reminder_status)}
                                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
                                    >
                                      <CheckCircle2 size={13} />
                                      Completed
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleToggleReminder(note.id, note.reminder_status)}
                                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                                    >
                                      <CheckCheck size={13} />
                                      Mark Complete
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Delete reminder"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>

        {/* Right Column - Appointments + Emails */}
        <div className="lg:col-span-2 order-3 lg:order-2 space-y-0 md:space-y-6">
          <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden border-b md:border-b-0 border-gray-200">
            <div className="bg-gray-50 px-3 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-pest-blue">Appointments</h2>
                <span className="bg-pest-blue text-white px-2 py-1 rounded-full text-sm">
                  {appointments.length}
                </span>
              </div>
              <button
                onClick={() => onAddAppointment({ jobId: job.id, job })}
                className="btn btn-md btn-secondary hidden md:flex"
              >
                <Plus size={16} />
                <span>Add Appointment</span>
              </button>
            </div>

            <div className="p-2 sm:p-6">
              {loadingAppointments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading appointments...</p>
                </div>
              ) : appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 mb-4">No appointments scheduled yet</p>
                  <button
                    onClick={() => onAddAppointment({ jobId: job.id, job })}
                    className="text-pest-blue hover:text-pest-teal flex items-center space-x-1 mx-auto transition-colors duration-200"
                  >
                    <Plus size={16} />
                    <span>Add First Appointment</span>
                  </button>
                </div>
              ) : (
                <AppointmentTimeline
                  appointments={[...futureAppointments, ...pastAppointments]}
                  onStatusChange={handleStatusChange}
                  onClick={handleEditAppointment}
                  showJobTitle={false}
                  showCustomerName={false}
                  maxItems={5}
                  renderDocs={(appointment) => {
                    const aptDocs = getAppointmentDocs(appointment.id);
                    if (aptDocs.length === 0) return null;
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200/60">
                        <div className="flex flex-wrap gap-1.5">
                          {aptDocs.map(doc => (
                            <button key={doc.id} type="button" onClick={async (e) => {
                              e.stopPropagation();
                              if (doc._type === 'report') handleViewReportPdf(doc);
                              if (doc._type === 'invoice') {
                                try {
                                  const fId = getFranchiseeIdForDataLoading(user);
                                  await invoiceService.viewPDF(fId, doc.id);
                                } catch (err) { toast.error('Failed to open invoice PDF'); }
                              }
                            }} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                              (doc._type === 'report' || doc._type === 'invoice') ? 'cursor-pointer hover:opacity-80' : ''
                            } ${
                              doc._type === 'invoice' ? 'bg-green-50 text-green-700' :
                              doc._type === 'report' ? 'bg-blue-50 text-blue-700' :
                              doc._type === 'estimate' ? 'bg-teal-50 text-teal-700' :
                              'bg-indigo-50 text-indigo-700'
                            }`}>
                              {doc._type === 'invoice' && <><FileText size={11} /> Inv {doc.invoice_number || doc.id.slice(0,6)}</>}
                              {doc._type === 'report' && <><ClipboardList size={11} /> Report {doc.report_number || doc.id.slice(0,6)}{doc.customer_name_signed && <Check size={10} className="text-green-600" />}</>}
                              {doc._type === 'estimate' && <><Calculator size={11} /> Est {doc.estimate_number || doc.id.slice(0,6)}</>}
                              {doc._type === 'guarantee' && <><Shield size={11} /> Guar {doc.guarantee_number || doc.id.slice(0,6)}</>}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                  renderActions={(appointment) => {
                    const apt = appointments.find(a => a.id === appointment.id) || appointment;
                    return (
                      <AppointmentCardMenu
                        onEdit={() => handleEditAppointment(appointment)}
                        onDelete={() => handleDeleteAppointment(appointment)}
                        onAddInvoice={() => openAction(setShowInvoiceForm, apt)}
                        onAddEstimate={() => openAction(setShowEstimateForm, apt)}
                        onAddReport={() => openAction(setShowReportEditor, apt)}
                        onAddGuarantee={() => openAction(setShowGuaranteeForm, apt)}
                      />
                    );
                  }}
                />
              )}
            </div>
          </div>

          {/* Emails Section - directly under appointments */}
          <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden border-b md:border-b-0 border-gray-200">
            <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Mail size={20} className="text-pest-blue" />
                <h2 className="text-xl font-semibold text-pest-blue">Emails</h2>
                {jobEmails.length > 0 && (
                  <span className="bg-pest-blue text-white px-2 py-0.5 rounded-full text-xs">{jobEmails.length}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {jobEmails.length > 0 && onNavigate && customer && (
                  <button
                    onClick={() => onNavigate(VIEWS.EMAILS, null, { emailSearch: customer.customer_ref || customer.name })}
                    className="text-sm text-pest-blue hover:text-pest-teal hover:underline flex items-center gap-1"
                  >
                    View all <ExternalLink size={13} />
                  </button>
                )}
                <button
                  onClick={() => { setComposeEmailPrefill(customer?.email || ''); setShowComposeEmail(true); }}
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 bg-pest-blue text-white text-sm font-medium rounded-lg hover:bg-pest-teal transition-colors"
                >
                  <Plus size={14} /> Email Customer
                </button>
              </div>
            </div>
            <div className="p-3 sm:p-6">
              {loadingEmails ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pest-blue mx-auto"></div>
                  <p className="text-gray-500 mt-2 text-sm">Loading emails...</p>
                </div>
              ) : jobEmails.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">No emails sent yet</p>
              ) : (
                <div className="space-y-2">
                  {jobEmails.slice(0, 5).map(email => (
                    <div
                      key={email.id}
                      className="flex items-center gap-3 py-2 px-3 rounded hover:bg-gray-50 text-sm cursor-pointer border border-gray-100"
                      onClick={() => onNavigate && onNavigate(VIEWS.EMAILS, null, { emailSearch: customer.customer_ref || customer.name })}
                    >
                      <div className="flex-shrink-0 w-5">
                        {email.read_date ? (
                          <CheckCheck size={15} className="text-green-500" />
                        ) : (
                          <Send size={14} className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          <span className="text-gray-500">Subject:</span> {email.subject}
                        </p>
                        <p className="text-xs text-gray-500">To: {email.to_email}</p>
                      </div>
                      {email.attachment_count > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-500 flex-shrink-0">
                          <Paperclip size={12} />
                          {email.attachment_count}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">{formatEmailDate(email.date_sent)}</span>
                    </div>
                  ))}
                  {jobEmails.length > 5 && (
                    <button
                      onClick={() => onNavigate && onNavigate(VIEWS.EMAILS, null, { emailSearch: customer.customer_ref || customer.name })}
                      className="text-sm text-pest-blue hover:text-pest-teal hover:underline w-full text-center py-2"
                    >
                      See {jobEmails.length - 5} more
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="order-4 lg:order-3 lg:col-start-1 lg:row-start-2">
          <div className="bg-white md:rounded-lg md:shadow-md overflow-hidden border-b md:border-b-0 border-gray-200">
            <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText size={20} className="text-pest-blue" />
                <h2 className="text-xl font-semibold text-pest-blue">Documents</h2>
              </div>
              <div className="hidden md:block"><AddDocHeaderButton /></div>
            </div>
            <div className="p-3 sm:p-6">
              {loadingDocuments ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pest-blue mx-auto"></div>
                  <p className="text-gray-500 mt-2 text-sm">Loading documents...</p>
                </div>
              ) : !documents || (documents.invoices.length === 0 && documents.treatmentReports.length === 0 && (documents.estimates || []).length === 0 && documents.guarantees.length === 0) ? (
                <p className="text-center text-gray-400 text-sm py-4">No documents yet</p>
              ) : (
                <div className="space-y-3">
                  {documents.invoices.map(inv => {
                    const apt = getDocAppointment(inv);
                    return (
                    <div key={inv.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <FileText size={16} className="text-green-600 flex-shrink-0" />
                            <span className="font-semibold text-gray-800">Invoice {inv.invoice_number || inv.id.slice(0,8)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500">
                            <span>Created: {new Date(inv.created_at || inv.invoice_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {inv.total_amount != null && (
                              <span className="font-medium text-gray-600">{'\u00A3'}{parseFloat(inv.total_amount).toFixed(2)}</span>
                            )}
                            {inv.status && (
                              <span className={`px-1.5 py-0.5 rounded-full font-medium capitalize ${inv.status === 'paid' ? 'text-green-600 bg-green-50' : inv.status === 'overdue' ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-100'}`}>{inv.status}</span>
                            )}
                          </div>
                        </div>
                        <DocActionMenu doc={inv} docType="invoice" />
                      </div>
                    </div>
                    );
                  })}
                  {(documents.estimates || []).map(est => {
                    return (
                    <div key={est.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Calculator size={16} className="text-teal-600 flex-shrink-0" />
                            <span className="font-semibold text-gray-800">Estimate {est.estimate_number || est.id.slice(0,8)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500">
                            <span>Created: {new Date(est.created_at || est.estimate_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {est.total_amount != null && (
                              <span className="font-medium text-gray-600">{'\u00A3'}{parseFloat(est.total_amount).toFixed(2)}</span>
                            )}
                            {est.status && (
                              <span className={`px-1.5 py-0.5 rounded-full font-medium capitalize ${est.status === 'active' ? 'text-green-600 bg-green-50' : est.status === 'declined' ? 'text-red-600 bg-red-50' : est.status === 'expired' ? 'text-amber-600 bg-amber-50' : est.status === 'converted' ? 'text-purple-600 bg-purple-50' : 'text-teal-600 bg-teal-50'}`}>{est.status}</span>
                            )}
                          </div>
                        </div>
                        <DocActionMenu doc={est} docType="estimate" />
                      </div>
                    </div>
                    );
                  })}
                  {documents.treatmentReports.map(tr => {
                    return (
                    <div key={tr.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <ClipboardList size={16} className="text-blue-600 flex-shrink-0" />
                            <span className="font-semibold text-gray-800">Report {tr.report_number || tr.id.slice(0,8)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500">
                            <span>Created: {new Date(tr.created_at || tr.service_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {tr.customer_name_signed ? (
                              <span className="inline-flex items-center gap-0.5 text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full font-medium">
                                <Check size={10} />
                                Signed
                              </span>
                            ) : (
                              <span className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium">Unsigned</span>
                            )}
                          </div>
                        </div>
                        <DocActionMenu doc={tr} docType="report" />
                      </div>
                    </div>
                    );
                  })}
                  {documents.guarantees.map(g => {
                    return (
                    <div key={g.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Shield size={16} className="text-indigo-600 flex-shrink-0" />
                            <span className="font-semibold text-gray-800">Guarantee {g.guarantee_number || g.id.slice(0,8)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500">
                            <span>Created: {new Date(g.created_at || g.date_issued).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {g.status && (
                              <span className={`px-1.5 py-0.5 rounded-full font-medium capitalize ${g.status === 'active' ? 'text-green-600 bg-green-50' : g.status === 'expired' ? 'text-red-600 bg-red-50' : 'text-indigo-600 bg-indigo-50'}`}>{g.status}</span>
                            )}
                          </div>
                        </div>
                        <DocActionMenu doc={g} docType="guarantee" />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Appointment Modal */}
      {editingAppointment && (
        <AddAppointmentModal
          onClose={() => setEditingAppointment(null)}
          onSubmit={handleUpdateAppointment}
          user={user}
          initialData={{ jobId: job.id }}
          editingAppointment={editingAppointment}
        />
      )}
      <ConfirmDialog {...confirmDialog} />

      {/* Compose Email Modal */}
      {showComposeEmail && (
        <ComposeEmailModal
          customer={customer}
          user={user}
          jobs={[job]}
          prefillEmail={composeEmailPrefill}
          onClose={() => { setShowComposeEmail(false); setComposeEmailPrefill(''); }}
          onSent={() => { setShowComposeEmail(false); setComposeEmailPrefill(''); loadEmails(); }}
        />
      )}

      {/* Document Form Modals */}
      {showInvoiceForm && (
        <InvoiceForm
          onClose={() => { setShowInvoiceForm(false); setEditingInvoice(null); setActionContext({ appointment: null }); }}
          onSubmit={handleInvoiceSubmit}
          invoice={editingInvoice}
          isEditing={!!editingInvoice}
          preselectedCustomer={customer}
          preselectedJob={job}
          preselectedAppointment={actionContext.appointment}
          showCustomerInTitle={true}
          user={user}
        />
      )}
      {showEstimateForm && (
        <InvoiceForm
          mode="estimate"
          onClose={() => { setShowEstimateForm(false); setEditingEstimate(null); setActionContext({ appointment: null }); }}
          onSubmit={handleEstimateSubmit}
          invoice={editingEstimate}
          isEditing={!!editingEstimate}
          preselectedCustomer={customer}
          preselectedJob={job}
          preselectedAppointment={actionContext.appointment}
          showCustomerInTitle={true}
          user={user}
        />
      )}
      {showGuaranteeForm && (
        <WoodwormGuaranteeForm
          onClose={() => { setShowGuaranteeForm(false); setActionContext({ appointment: null }); }}
          onSubmit={handleGuaranteeSubmit}
          preselectedCustomer={customer}
          preselectedJob={job}
          preselectedAppointment={actionContext.appointment}
          user={user}
        />
      )}
      {showReportEditor && (
        <TreatmentReportEditor
          report={editingReport}
          onSave={handleReportSave}
          onSaveAndSign={handleSaveAndSign}
          onCancel={() => { setShowReportEditor(false); setEditingReport(null); setActionContext({ appointment: null }); }}
          userProfile={userProfile || user}
          initialContext={{
            customer,
            job,
            appointment: actionContext.appointment
          }}
        />
      )}
      {signingReport && (
        <SignatureModal
          report={signingReport}
          onSign={handleSignatureSubmit}
          onClose={() => setSigningReport(null)}
          saving={signingSaving}
        />
      )}
    </div>
  );
};

export default JobDetails;
